import { Badge, Box, Flex, Tooltip } from "@chakra-ui/react"
import { FilterChip } from "component/FilterChip"
import { SMRSectionSelect } from "component/mapping/result/SMRSectionSelect"
import { EyeOff } from "lucide-react"
import { FC } from "react"

export type SmrStatusFilter = "all" | "unchecked" | "notFound"

export type SmrFilterOption<T extends string = string> = {
  value: T
  label: string
  count: number
}

interface SMRResultsFiltersProps {
  // Тип и раздел — только у сметы; у УПД одни статусы
  types?: SmrFilterOption[]
  // Типы, которые ещё не сопоставляем: видны, но неактивны
  soonTypes?: string[]
  type?: string
  onTypeChange?: (type: string) => void
  sections?: SmrFilterOption[]
  section?: string
  onSectionChange?: (section: string) => void
  statuses: SmrFilterOption<SmrStatusFilter>[]
  status: SmrStatusFilter
  onStatusChange: (status: SmrStatusFilter) => void
  // Смета: спрятать строки, где название в смете = материал НСИ (KRE-758).
  // Не вместо статуса, а вместе с ним; count — сколько строк он прячет
  sameAsNsiCount?: number
  // Подсказка: "материал" или "работа" — по типу позиции, как в колонках
  sameAsNsiHint?: string
  isSameAsNsiHidden?: boolean
  onSameAsNsiHiddenChange?: (isHidden: boolean) => void
}

const withCount = (option: SmrFilterOption) => `${option.label} · ${option.count}`

// Фильтры страницы проверки: тип позиции и раздел (смета), статус проверки
// (смета и УПД). Таблица плоская — сотни строк разбирают фильтрами, а не прокруткой
export const SMRResultsFilters: FC<SMRResultsFiltersProps> = (props) => {
  const {
    types,
    soonTypes = [],
    type,
    onTypeChange,
    sections,
    section,
    onSectionChange,
    statuses,
    status,
    onStatusChange,
    sameAsNsiCount,
    sameAsNsiHint,
    isSameAsNsiHidden = false,
    onSameAsNsiHiddenChange,
  } = props

  return (
    <Flex w="full" wrap="wrap" align="center" gap={2}>
      {types && sections && (
        <>
          {/* Тип позиции: Материалы, позже Работы */}
          {types.map((option) => (
            <FilterChip
              key={option.value}
              isActive={option.value === type}
              onClick={() => onTypeChange?.(option.value)}
            >
              {withCount(option)}
            </FilterChip>
          ))}

          {soonTypes.map((soonType) => (
            <Tooltip key={soonType} label="Сопоставление работ появится позже" hasArrow>
              <Flex
                align="center"
                gap={2}
                h={8}
                px={3}
                borderRadius="full"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                color="gray.400"
                fontSize="sm"
                fontWeight="medium"
                cursor="default"
              >
                {soonType}
                <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2}>
                  Скоро
                </Badge>
              </Flex>
            </Tooltip>
          ))}

          <Box w="1px" h={6} bg="gray.200" mx={1} />

          {/* Раздел сметы */}
          <SMRSectionSelect
            options={sections}
            value={section ?? ""}
            onChange={(value) => onSectionChange?.(value)}
          />

          <Box w="1px" h={6} bg="gray.200" mx={1} />
        </>
      )}

      {/* Статус проверки */}
      {statuses.map((option) => (
        <FilterChip
          key={option.value}
          isActive={option.value === status}
          onClick={() => onStatusChange(option.value)}
        >
          {withCount(option)}
        </FilterChip>
      ))}

      {/* Совпадающие с НСИ: в длинном списке одинаковых плит строку, где
          модель выбрала другой материал, легко проморгать */}
      {onSameAsNsiHiddenChange && (
        <>
          <Box w="1px" h={6} bg="gray.200" mx={1} />

          <Tooltip
            label={sameAsNsiHint}
            hasArrow
            openDelay={500}
            shouldWrapChildren
          >
            <FilterChip
              isActive={isSameAsNsiHidden}
              onClick={() => onSameAsNsiHiddenChange(!isSameAsNsiHidden)}
              leftIcon={<EyeOff size={14} />}
            >
              {`Скрыть совпадающие с НСИ · ${sameAsNsiCount ?? 0}`}
            </FilterChip>
          </Tooltip>
        </>
      )}
    </Flex>
  )
}
